import React from 'react';
import { motion } from 'framer-motion';

const floatingOrbVariants = {
  animate: {
    y: [0, -12, 0],
    opacity: [0.35, 0.6, 0.35],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

const SubscriptionGate = ({
  title = 'Full Subscription Required',
  description = 'Unlock predictive insights and conversational AI by upgrading your plan.',
  cta = 'Upgrade Plan',
}) => {
  return (
    <div className="relative flex min-h-[60vh] items-center justify-center overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#141923] via-[#0C1017] to-[#070A0F] p-10 text-white shadow-2xl">
      <motion.div
        className="pointer-events-none absolute -left-10 top-10 h-48 w-48 rounded-full bg-cyan-500/40 blur-3xl"
        variants={floatingOrbVariants}
        animate="animate"
      />
      <motion.div
        className="pointer-events-none absolute -right-12 bottom-6 h-56 w-56 rounded-full bg-indigo-500/40 blur-3xl"
        variants={floatingOrbVariants}
        animate="animate"
        transition={{ delay: 1.5 }}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="relative z-10 flex max-w-xl flex-col items-center gap-6 text-center"
      >
        <motion.div
          className="relative flex h-24 w-24 items-center justify-center"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.45, delay: 0.1, ease: 'backOut' }}
        >
          <span className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/80 to-indigo-500/80 blur-lg opacity-80" />
          <span className="absolute inset-2 rounded-full border border-white/20 bg-white/10 backdrop-blur" />
          <motion.span
            animate={{ rotate: 360 }}
            transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
            className="absolute -inset-1 rounded-full border border-dashed border-white/30"
          />
          <motion.svg
            initial={{ y: 6 }}
            animate={{ y: [-2, 6, -2] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            className="h-10 w-10 text-white"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="M17 9V7a5 5 0 0 0-10 0v2M6 9h12l1 12H5L6 9Z"
            />
          </motion.svg>
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.35 }}
          className="text-3xl font-semibold tracking-tight"
        >
          {title}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18, duration: 0.35 }}
          className="text-base text-white/70"
        >
          {description}
        </motion.p>
        <motion.button
          type="button"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
          className="group relative mt-2 inline-flex items-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-primary via-sky-500 to-indigo-500 px-8 py-3 text-sm font-semibold uppercase tracking-widest"
        >
          <span className="relative z-10">{cta}</span>
          <span className="absolute inset-0 -z-0 bg-white/15 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
          <motion.span
            className="relative z-10 flex h-2 w-2 items-center justify-center"
            animate={{
              scale: [1, 1.4, 1],
              opacity: [0.6, 1, 0.6],
            }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <span className="h-2 w-2 rounded-full bg-white" />
          </motion.span>
        </motion.button>
      </motion.div>
    </div>
  );
};

export default SubscriptionGate;
